import type { AcademicTerm, Classroom } from "@classroom/domain";
import { studentNumberLabel } from "@classroom/domain";
import { type RefObject, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "./i18n";
import {
  buildScopedRecordReport,
  type ExportRange,
  encodeDelimitedSheet,
  type RecordExportScope,
  type RecordExportSource,
  validateExportRange,
} from "./lib/localRecordExport";
import { RECORD_EXPORT_FORMATS } from "./lib/recordExportFormats";
import { type ExportFormat, exportRecordReport, type RecordExportTab, type RecordReport } from "./lib/recordExports";
import { printRecordReport } from "./lib/recordPrint";
import RecordActionButton, { RecordActionIcon } from "./RecordActionButton";
import RecordPdfPreview from "./RecordPdfPreview";
import StudentPagination, { useStudentPagination } from "./StudentPagination";

type Props = {
  dialogRef: RefObject<HTMLElement | null>;
  tab: RecordExportTab;
  classItem: Classroom;
  source: RecordExportSource;
  studentId?: string;
  scope?: RecordExportScope;
  term?: AcademicTerm;
  busy: boolean;
  onBusyChange: (busy: boolean) => void;
  onClose: () => void;
  onPrintError: (message: string) => void;
};

function downloadFile(contents: string, filename: string, type: string) {
  const url = URL.createObjectURL(new Blob([contents], { type }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename.replace(/[\\/:*?"<>|]/gu, "-");
  document.body.append(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export function RecordExportDialog({
  dialogRef,
  tab,
  classItem,
  source,
  studentId,
  scope,
  term,
  busy,
  onBusyChange,
  onClose,
  onPrintError,
}: Props) {
  const { t, i18n } = useTranslation("localExport");
  const printing = scope?.intent === "print";
  const printStarted = useRef(false);
  const pdfTrigger = useRef<HTMLButtonElement>(null);
  const [pdfReport, setPdfReport] = useState<RecordReport | null>(null);
  const [range, setRange] = useState<ExportRange>(scope?.range || {});
  const [sheetIndex, setSheetIndex] = useState(0);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const rangeError = validateExportRange(range, term);
  const filteredSource = useMemo(
    () => ({
      ...source,
      students: scope?.studentIds
        ? source.students.filter((item) => scope.studentIds?.includes(item.id))
        : source.students,
      dailyRecords: scope?.studentIds
        ? source.dailyRecords.map((item) => ({
            ...item,
            students: Object.fromEntries(
              Object.entries(item.students).filter(([id]) => scope.studentIds?.includes(id)),
            ),
          }))
        : source.dailyRecords,
      homeworkAssignments: scope?.assignmentIds
        ? source.homeworkAssignments.filter((item) => scope.assignmentIds?.includes(item.id))
        : source.homeworkAssignments,
      homeworkRecords: scope?.assignmentIds
        ? source.homeworkRecords.filter((item) => scope.assignmentIds?.includes(item.assignmentId))
        : source.homeworkRecords,
      homeroomHomeworkRecords: scope?.homeworkRecordIds
        ? source.homeroomHomeworkRecords.filter((item) => scope.homeworkRecordIds?.includes(item.id))
        : source.homeroomHomeworkRecords,
    }),
    [source, scope],
  );
  const report = useMemo(() => {
    if (scope?.report) return scope.report;
    if (rangeError) return undefined;
    const result = buildScopedRecordReport(tab, classItem, filteredSource, range, studentId, term);
    if (scope?.sheetTitles) result.sheets = result.sheets.filter((item) => scope.sheetTitles?.includes(item.title));
    if (scope?.label) result.title += `｜${scope.label}`;
    return result;
  }, [tab, classItem, filteredSource, range, studentId, term, rangeError, scope, i18n.resolvedLanguage]);
  const sheet = report?.sheets[sheetIndex];
  const student = source.students.find((item) => item.id === studentId);
  const exportTarget =
    scope?.label || (scope?.sheetTitles?.length === 1 ? scope.sheetTitles[0] : t(`recordTypes.${tab}`));
  const previewRows = useMemo(() => {
    const occurrences = new Map<string, number>();
    return (sheet?.rows || []).map((cells) => {
      const signature = JSON.stringify(cells);
      const occurrence = occurrences.get(signature) || 0;
      occurrences.set(signature, occurrence + 1);
      return { key: `${signature}:${occurrence}`, cells };
    });
  }, [sheet]);
  const previewPagination = useStudentPagination(
    previewRows,
    JSON.stringify([classItem.id, sheetIndex, range, studentId, term?.id]),
  );
  const invalid = Boolean(rangeError || !sheet);
  const canPrint =
    Boolean(scope?.printReport) ||
    ["roster", "seating", "homeroomDuties", "attendance", "homework", "homeroomHomework", "studentRecords"].includes(
      tab,
    );
  const hasDates = !scope?.report && !["roster", "seating", "homeroomDuties"].includes(tab);
  const pdfOnly = ["homeroomDuties", "seating"].includes(tab);
  const previewReport = pdfReport || (pdfOnly ? scope?.printReport || report : undefined);

  const perform = useCallback(
    async (action: "csv" | "copy" | "print" | ExportFormat) => {
      if (busy || invalid || !sheet || !report) return;
      setError("");
      setNotice("");
      if (action === "pdf") {
        setPdfReport(scope?.printReport || report);
        return;
      }
      onBusyChange(true);
      try {
        if (action === "print")
          await printRecordReport(scope?.printReport || report, {
            board: t("printBoard"),
            teacher: t("printTeacher"),
            student: t("printStudent"),
            emptySeat: t("printEmptySeat"),
            noSeating: t("printNoSeating"),
          });
        else if (action === "csv")
          downloadFile(
            encodeDelimitedSheet(sheet, "csv"),
            `${classItem.name}-${report.title}-${sheet.title}.csv`,
            "text/csv;charset=utf-8",
          );
        else if (action === "copy") await navigator.clipboard.writeText(encodeDelimitedSheet(sheet, "tsv"));
        else await exportRecordReport(report, action);
        if (printing) onClose();
        else setNotice(t(action === "print" ? "printOpened" : action === "copy" ? "copied" : "downloaded"));
      } catch {
        const message = t(action === "print" ? "printFailed" : action === "copy" ? "copyFailed" : "failed");
        if (printing) {
          onPrintError(message);
          onClose();
        } else setError(message);
      } finally {
        onBusyChange(false);
      }
    },
    [busy, invalid, sheet, report, scope, printing, classItem.name, t, onBusyChange, onClose, onPrintError],
  );

  useEffect(() => {
    if (!printing || busy || printStarted.current) return;
    printStarted.current = true;
    if (invalid) {
      onPrintError(t("printFailed"));
      onClose();
    } else void perform("print");
  }, [printing, busy, invalid, perform, onPrintError, onClose, t]);

  if (printing) return null;

  return (
    <section
      ref={dialogRef}
      tabIndex={-1}
      className="paper-card record-export-dialog local-export-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="record-export-title"
    >
      <header className="local-export-header">
        <h2 id="record-export-title">{t("exportTarget", { target: exportTarget })}</h2>
        <button type="button" className="record-export-close" aria-label={t("close")} disabled={busy} onClick={onClose}>
          ×
        </button>
      </header>
      <p className="local-export-scope">
        {classItem.name}
        {term && (
          <>
            {" "}
            · {term.schoolYear} {term.name}
          </>
        )}
        {student && (
          <>
            {" "}
            · {studentNumberLabel(student)} {student.name}
          </>
        )}
      </p>
      {pdfOnly || pdfReport ? (
        previewReport ? (
          <RecordPdfPreview
            report={previewReport}
            onBusyChange={onBusyChange}
            onBack={
              pdfOnly
                ? undefined
                : () => {
                    setPdfReport(null);
                    requestAnimationFrame(() => pdfTrigger.current?.focus());
                  }
            }
          />
        ) : (
          <p className="form-error" role="alert">
            {t("pdfPreviewFailed")}
          </p>
        )
      ) : (
        <>
          {hasDates && (
            <fieldset disabled={busy} className="local-export-date-fields">
              <legend>{t("rangeTitle")}</legend>
              <label>
                <span className="sr-only">{t("from")}</span>
                <input
                  className="local-export-date-input"
                  type="date"
                  min={term?.startDate}
                  max={term?.endDate}
                  value={range.from || ""}
                  onChange={(event) => {
                    setRange({ ...range, from: event.target.value });
                    setSheetIndex(0);
                  }}
                />
              </label>
              <span className="local-export-date-separator" aria-hidden="true">
                {t("dateSeparator")}
              </span>
              <label>
                <span className="sr-only">{t("to")}</span>
                <input
                  className="local-export-date-input"
                  type="date"
                  min={term?.startDate}
                  max={term?.endDate}
                  value={range.to || ""}
                  onChange={(event) => {
                    setRange({ ...range, to: event.target.value });
                    setSheetIndex(0);
                  }}
                />
              </label>
            </fieldset>
          )}
          {rangeError && (
            <p className="form-error" role="alert">
              {t(`errors.${rangeError}`)}
            </p>
          )}
          {report && report.sheets.length > 1 && (
            <label>
              {t("sheet")}
              <select
                aria-label={t("sheet")}
                disabled={busy}
                value={sheetIndex}
                onChange={(event) => setSheetIndex(Number(event.target.value))}
              >
                {report.sheets.map((item, index) => (
                  <option key={item.title} value={index}>
                    {item.title}
                  </option>
                ))}
              </select>
            </label>
          )}
          {sheet && (
            <>
              <p className="local-export-note">{t("rowCount", { count: sheet.rows.length })}</p>
              <div
                className="local-export-table-scroll"
                role="region"
                aria-label={t("preview")}
                // biome-ignore lint/a11y/noNoninteractiveTabindex: Keyboard focus permits scrolling the report table.
                tabIndex={0}
              >
                <table className="local-export-table" aria-label={t("preview")}>
                  <thead>
                    <tr>
                      {sheet.headers.map((header) => (
                        <th className="local-export-cell local-export-column" key={header} scope="col">
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewPagination.items.map(({ key, cells }) => (
                      <tr key={key}>
                        {cells.map((cell, column) => (
                          <td className="local-export-cell" key={sheet.headers[column]}>
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <StudentPagination {...previewPagination} disabled={busy} />
            </>
          )}
          {error && (
            <p className="form-error" role="alert">
              {error}
            </p>
          )}
          {notice && <p role="status">{notice}</p>}
          <div className="record-export-formats">
            {RECORD_EXPORT_FORMATS.filter(
              (format) => !["seating", "homeroomDuties"].includes(tab) || format === "pdf",
            ).map((format) => (
              <button
                ref={format === "pdf" ? pdfTrigger : undefined}
                key={format}
                type="button"
                disabled={busy || invalid}
                onClick={() => void perform(format)}
              >
                <RecordActionIcon action="export" />
                <strong>{t(`formats.${format}`)}</strong>
                <small>.{format}</small>
              </button>
            ))}
          </div>
          <div className="local-export-actions">
            {canPrint && (
              <RecordActionButton action="print" disabled={busy || invalid} onClick={() => void perform("print")}>
                {t("print")}
              </RecordActionButton>
            )}
            {!["seating", "homeroomDuties"].includes(tab) && (
              <button type="button" disabled={busy || invalid} onClick={() => void perform("copy")}>
                {t("copy")}
              </button>
            )}
          </div>
          {busy && <p role="status">{t("working")}</p>}
        </>
      )}
    </section>
  );
}
